<template>
    <kiosk-layout formContainer>
        <page-loading v-if="loading"></page-loading>
        <div v-else>
            <page-header class="mb-6 lg:mb-12">
                <template v-slot:subtitle>
                    <a href="/kiosk/groups" class="no-underline! text-sky-600! text-base font-normal">
                        <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                        Search
                    </a>
                </template>

                <div class="flex items-center">
                    {{ group.name }}
                    <group-status-badge v-if="group.status" :status="group.status" class="ms-3" />
                </div>

                <template v-slot:actions>
                    <vx-button
                        color="primary"
                        :href="
                            team
                                ? `/spark/kiosk/users/impersonate/${team.owner_id}?team_id=${props.teamId}&group_id=${props.groupId}`
                                : '#'
                        "
                        :disabled="!team || team.owner_id === currentUserId || team.status === 'closed'"
                    >
                        Impersonate Owner <font-awesome-icon widthAuto :icon="faMasksTheater"></font-awesome-icon>
                    </vx-button>
                </template>
            </page-header>

            <div class="grid grid-cols-12">
                <page-sidebar :links="navigationLinks"></page-sidebar>

                <slot></slot>
            </div>
        </div>
    </kiosk-layout>
</template>

<script setup>
import { faArrowLeft, faMasksTheater } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { onMounted, computed } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageSidebar from '~/components/general/PageSidebar.vue';
import { VxButton } from '@voxie/frontend-components';
import { useGroupDetails } from '../composables/useGroupDetails';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
});

const { mount, group, loading, team } = useGroupDetails();

const currentUserId = Spark.state.user.id;

const navigationLinks = computed(() => {
    const links = [
        { href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}`, label: 'Details' },
        { href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/configuration`, label: 'Configuration' },
    ];

    if (!team.value && loading.value) {
        return links;
    }

    links.push({
        href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/status-log`,
        label: 'Status Log',
    });

    // eslint-disable-next-line no-restricted-syntax
    const teamHasNewBilling = team.value?.team_attributes?.new_billing_implementation || false;
    if (teamHasNewBilling) {
        links.push({
            href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/billing-config`,
            label: 'Billing Config',
        });

        if (group.value?.is_billable) {
            links.push({
                href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/billing-details`,
                label: 'Billing Details',
            });

            if (team.value?.status !== 'closed') {
                links.push({
                    href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/proforma-invoices`,
                    label: 'Upcoming Invoices',
                });
            }

            links.push({
                href: `/kiosk/teams/${props.teamId}/groups/${props.groupId}/historical-invoices`,
                label: 'Historical Invoices',
            });
        }
    }

    return links;
});

onMounted(() => {
    mount(props.teamId, props.groupId);
});
</script>
